import { MEDIA_QUERY_STATES } from '@constants/media-query';
import { useMedia } from '@tkww/orion-web-app-kernel';
import { Carousel, Tooltip, TooltipContainer } from '@tkww/orion-web-ds';
import type { IconName } from '@tkww/orion-web-ds-graphic';
import { Icon } from '@tkww/orion-web-ds-graphic';
import { TextBody } from '@tkww/orion-web-ds-text';
import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useTrackPricingFactorsCarousel } from '../../hooks/useTrackPricingFactorsCarousel';
import { PricingFactorCard } from '../PricingFactorCard';
import Styles from './styles.scss';

const PRICING_FACTORS: Array<{
	icon: IconName;
	title: string;
	description: string;
}> = [
	{
		icon: 'group',
		title: 'Guest count',
		description:
			'Even a 10% reduction can make a huge impact on per guest costs.',
	},
	{
		icon: 'calendar',
		title: 'Seasonality',
		description:
			'Prices spike during peak season. Check out off-peak season dates and compare.',
	},
	{
		icon: 'calendar-check',
		title: 'Day of week',
		description:
			'Saturdays tend to be more expensive. Consider a Friday or Sunday.',
	},
	{
		icon: 'clock',
		title: 'Access time',
		description:
			'See how you can simplify your decor to reduce setup time needed in the space.',
	},
];

const TitleWithTooltip = () => {
	const [isTooltipOpen, setIsTooltipOpen] = useState(false);
	const [arrowOffset, setArrowOffset] = useState(50);

	const buttonRef = useRef<HTMLButtonElement>(null);
	const tooltipRef = useRef<HTMLDivElement>(null);

	const updateArrowPosition = useCallback(() => {
		if (!buttonRef.current || !tooltipRef.current) return;

		const buttonRect = buttonRef.current.getBoundingClientRect();
		const tooltipRect = tooltipRef.current.getBoundingClientRect();

		if (!tooltipRect.width) return;

		const tooltipStyles = window.getComputedStyle(tooltipRef.current);
		const paddingLeft = Number.parseFloat(tooltipStyles.paddingLeft || '0');
		const paddingRight = Number.parseFloat(tooltipStyles.paddingRight || '0');
		const contentWidth = tooltipRect.width - paddingLeft - paddingRight;

		if (contentWidth <= 0) return;

		const buttonCenter = buttonRect.left + buttonRect.width / 2;
		const relativePosition = buttonCenter - (tooltipRect.left + paddingLeft);
		const percentage = Math.max(
			0,
			Math.min(100, (relativePosition / contentWidth) * 100),
		);

		setArrowOffset(Math.round(percentage));
	}, []);

	useLayoutEffect(() => {
		if (!isTooltipOpen) return;

		const animationFrameId = requestAnimationFrame(updateArrowPosition);

		window.addEventListener('resize', updateArrowPosition);
		window.addEventListener('orientationchange', updateArrowPosition);

		return () => {
			cancelAnimationFrame(animationFrameId);
			window.removeEventListener('resize', updateArrowPosition);
			window.removeEventListener('orientationchange', updateArrowPosition);
		};
	}, [updateArrowPosition, isTooltipOpen]);

	return (
		<>
			<div className={Styles.titleWithTooltip}>
				<TextBody size="xl" bold className={Styles.title}>
					What affects venue prices
				</TextBody>
				<button
					ref={buttonRef}
					type="button"
					className={Styles.iconButton}
					onMouseEnter={() => setIsTooltipOpen(true)}
					onMouseLeave={() => setIsTooltipOpen(false)}
					onFocus={() => setIsTooltipOpen(true)}
					onBlur={() => setIsTooltipOpen(false)}
					aria-label="More information about pricing factors"
					data-testid="pricing-factors-tooltip-trigger"
				>
					<Icon size="sm" name="info" color="info" aria-label="Information" />
				</button>
			</div>
			<div className={Styles.tooltipWrapper}>
				<TooltipContainer className={Styles.tooltipContainer}>
					{isTooltipOpen && (
						<Tooltip
							arrowPosition="top"
							arrowOffset={arrowOffset}
							className={Styles.tooltip}
							ref={tooltipRef}
						>
							<TextBody size="sm">
								Tips on how to save may not apply to all venues.
							</TextBody>
						</Tooltip>
					)}
				</TooltipContainer>
			</div>
		</>
	);
};

const MOBILE_VISIBLE_SLIDES = 2.15;

const PricingFactorsCarousel = () => {
	const isMobile = useMedia('@mobile') === MEDIA_QUERY_STATES.matched;
	const slideWidth = 170;
	const slideGutter = 4;
	const totalSlides = PRICING_FACTORS.length;
	const minWidthForAllSlides =
		totalSlides * slideWidth +
		(totalSlides - 1) * slideGutter * 2 +
		slideGutter * 2;

	const handleNavigationChange = useTrackPricingFactorsCarousel();

	return (
		<Carousel
			minSlideWidth={slideWidth}
			gutter={slideGutter * 2}
			maxSlideWidth={slideWidth}
			responsive={({ width }) => {
				if (isMobile) return { slides: MOBILE_VISIBLE_SLIDES };
				if (width >= minWidthForAllSlides) return { slides: totalSlides };
				return { slides: width / (slideWidth + slideGutter * 2) };
			}}
			className={Styles.pricingFactorsCarousel}
			onNavigationIndexChange={handleNavigationChange}
		>
			{PRICING_FACTORS.map(({ icon, title, description }) => (
				<PricingFactorCard
					key={title}
					iconName={icon}
					title={title}
					description={description}
				/>
			))}
		</Carousel>
	);
};

export const PricingFactors = () => {
	return (
		<div
			className={Styles.pricingFactorsContainer}
			data-testid="pricing-factors-container"
		>
			<div className={Styles.pricingFactorsHeading}>
				<TitleWithTooltip />
				<TextBody size="md" color="subtle-dark">
					With tips on how to save
				</TextBody>
			</div>
			<div
				className={Styles.pricingFactorsContent}
				data-testid="pricing-factors-content"
			>
				<PricingFactorsCarousel />
			</div>
		</div>
	);
};
